import { useState, useEffect } from "react";
import {
  Modal,
  DatePicker,
  InputNumber,
  Checkbox,
  Button,
  Space,
  Popconfirm,
  Form,
  Typography,
  Segmented,
  Select,
  Alert,
} from "antd";
import dayjs from "dayjs";
import { apiFetch } from "../../services/apiClient";
import { configOptionApi } from "../../services/configOption.api";
import { clpInputFormatter, clpInputParser } from "../../utils/clpInput";
// Bridge antd estático: Modal/message/notification importados directo de "antd"
// no funcionan en v5 + React 19 (root huérfano fuera del ConfigProvider).
import { getMsg, getModal } from "../../utils/antdStatic";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import { excedeRangoTarifas, MENSAJE_RANGO_TARIFAS_MAXIMO } from "../../utils/rangoTarifas";

const { Text } = Typography;
const { RangePicker } = DatePicker;

export type MetaField = "base" | "condicion" | "minNoches" | "restriccion";

type BulkProperty = {
  id: number;
  nombre: string;
  idPropiedadCorto?: string | null;
  /** Precio mínimo por noche acordado con el propietario (null = sin mínimo). */
  precioMinimoNoche?: number | null;
};

/** Formatea un monto CLP para mensajes (sin decimales, con separador local). */
const clp = (n: number): string =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n);

// ─── Umbral global de precio bajo (red anti-error de tipeo) ────────────────
// Se lee UNA vez por sesión (cache a nivel de módulo) desde
// /config-options/general-settings. Si el backend no responde, apiFetch
// (silent) devuelve null y caemos al default 40.000 sin molestar con toasts.
// Es genérico para toda propiedad — distinto del mínimo acordado por
// propiedad. Ver docs/modulo-calendario.md (guardas de precio).
const UMBRAL_DEFAULT = 40000;
let _umbralCache: number | null = null;
let _umbralPromise: Promise<number> | null = null;
function loadUmbralPrecioBajo(): Promise<number> {
  if (_umbralCache != null) return Promise.resolve(_umbralCache);
  if (!_umbralPromise) {
    _umbralPromise = configOptionApi.getGeneralSettings().then((res) => {
      const v = res?.data?.umbralPrecioBajo;
      _umbralCache = typeof v === "number" && v >= 0 ? v : UMBRAL_DEFAULT;
      return _umbralCache;
    });
  }
  return _umbralPromise;
}

type Props = {
  open: boolean;
  onClose: () => void;
  field: MetaField | null;
  dateRange: [string, string] | null; // YYYY-MM-DD
  /** Edición inline (1 propiedad). Ignorado si se pasa `bulkProperties`. */
  propertyIds: number[];
  /** Texto para el encabezado en modo inline. */
  propertyLabel?: string | null;
  /**
   * Precio mínimo por noche acordado con el propietario de la propiedad en
   * edición inline (null = sin mínimo). Si al fijar el precio base se ingresa
   * un valor menor, el modal pide confirmación por pasar a llevar el acuerdo.
   * En modo masivo el mínimo viaja por propiedad en `bulkProperties`.
   */
  propertyMin?: number | null;
  /**
   * Modo masivo: si viene la lista, el modal muestra selector de campo +
   * multi-selección de propiedades. Las propiedades sin id corto de Stays se
   * filtran (no se pueden empujar). Fase 4 (cambio masivo).
   */
  bulkProperties?: BulkProperty[];
  /**
   * Condición vigente de la PRIMERA noche del rango (sólo inline + field=condicion).
   * Si viene, el modal precarga dirección/tipo/valor para NO invertir el signo al
   * re-editar (ej.: una noche con −20% abre en Promoción/−/20, no en Adición/+/vacío).
   * Hallazgo P2 review 2026-06-30.
   */
  initialCondicion?: { tipo: "porcentaje" | "monto"; valor: number } | null;
  /**
   * Mapa de precio efectivo por noche (YYYY-MM-DD → CLP | null) de la propiedad en
   * edición INLINE. Lo usa `minNoches` para detectar noches SIN precio: Stays exige
   * un precio para poder fijar el mínimo, así que si el rango incluye noches sin
   * precio, el modal ofrece un "Precio base" para asignárselo en el mismo paso.
   * Undefined en modo masivo (no hay mapa por-noche multi-propiedad).
   */
  nightPrices?: Record<string, number | null>;
  /**
   * Callback tras aplicar OK. Recibe el resultado del backend: si trae
   * `bloqueos: true` (precio 0 → se crearon bloqueos para inhabilitar), el
   * caller debe refrescar también los EVENTOS (la barra de bloqueo nueva),
   * no solo la meta. Round 2026-07-02.
   */
  onSuccess?: (result?: { bloqueos?: boolean }) => void;
};

const DAY_OPTIONS = [
  { label: "Lun", value: 1 },
  { label: "Mar", value: 2 },
  { label: "Mié", value: 3 },
  { label: "Jue", value: 4 },
  { label: "Vie", value: 5 },
  { label: "Sáb", value: 6 },
  { label: "Dom", value: 0 },
];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const PRESET_DJ = [0, 1, 2, 3, 4];
const PRESET_VS = [5, 6];

const FIELD_INFO: Record<MetaField, { title: string; help: string }> = {
  base: {
    title: "Editar precio base",
    help: "Precio base por noche. Lo que se envía a Stays es el EFECTIVO = base aplicado con la condición. Si la noche no tiene condición, el efectivo es igual al base. Precio 0 = INHABILITAR las noches: se crea un BLOQUEO en Stays para ese rango (los precios no se tocan); se revierte eliminando el bloqueo.",
  },
  condicion: {
    title: "Editar condición",
    help: "La condición es un ajuste sobre el precio base (promoción = baja, adición = alza), en % o en monto fijo. Vive solo en mialoja: a Stays se envía el precio efectivo (base + condición). El precio base y el mínimo de noches NO cambian. Si el ajuste dejara alguna noche con efectivo $0 o negativo, el cambio se bloquea (efectivo mínimo $1). Dejar el valor en 0 o vacío = SIN condición: el efectivo vuelve a ser igual al base (equivale a no tocar el precio base / quitar la condición vigente).",
  },
  minNoches: {
    title: "Editar mínimo de noches",
    help: "Mínimo de noches exigido para reservar estas fechas. Se sincroniza con Stays (ida y vuelta).",
  },
  restriccion: {
    title: "Editar restricción",
    help: "IN = cerrado a llegada (no se puede hacer check-in ese día). OUT = cerrado a salida. IN OUT = ambas. Se sincroniza con Stays (ida y vuelta).",
  },
};

const FIELD_OPTS = [
  { label: "Precio base", value: "base" },
  { label: "Condición", value: "condicion" },
  { label: "Min. noches", value: "minNoches" },
  { label: "Restricción", value: "restriccion" },
];

const RESTRICCION_OPTS = [
  { label: "Sin restricción", value: "none" },
  { label: "IN", value: "IN" },
  { label: "OUT", value: "OUT" },
  { label: "IN OUT", value: "IN OUT" },
];

export default function MetaEditModal({
  open,
  onClose,
  field,
  dateRange,
  propertyIds,
  propertyLabel,
  propertyMin,
  bulkProperties,
  initialCondicion,
  nightPrices,
  onSuccess,
}: Props) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // Umbral global de precio bajo (CLP). Se carga al abrir; mientras tanto usa
  // el default para no dejar la guardia "abierta".
  const [umbral, setUmbral] = useState<number>(UMBRAL_DEFAULT);
  const [condDir, setCondDir] = useState<"promo" | "adicion">("adicion");
  const [condTipo, setCondTipo] = useState<"porcentaje" | "monto">("porcentaje");
  // En modo masivo, el campo y las propiedades son estado interno.
  const [activeField, setActiveField] = useState<MetaField>(field ?? "base");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const isBulk = Array.isArray(bulkProperties);
  // Solo propiedades con id corto de Stays son empujables.
  const bulkOpts = (bulkProperties ?? [])
    .filter((p) => p.idPropiedadCorto)
    .map((p) => ({ value: p.id, label: formatPropertyName(p.nombre) || `Propiedad ${p.id}` }));

  // Cuántas noches abarca el rango elegido (inclusive, igual que el backend).
  // Se muestra para que el admin VEA si por error seleccionó más de una noche.
  const rango = Form.useWatch("rango", form);
  const nNoches =
    rango?.[0] && rango?.[1] ? Math.max(0, rango[1].diff(rango[0], "day")) + 1 : 0;
  // ¿El rango pasa de lo que el backend alcanza a leer del calendario de Stays
  // antes de escribir? Se avisa acá abajo y se bloquea el envío: si no, el admin
  // arma el rango a ciegas y se entera recién al aplicar, cuando cada propiedad
  // vuelve con `ok: false` y el mensaje del tope, sin que se escriba nada.
  // Ver utils/rangoTarifas.ts y docs/modulo-stays.md §7.2 («Lectura en tramos»).
  const rangoExcedido = excedeRangoTarifas(rango?.[0], rango?.[1]);
  // Precio base que el admin está escribiendo — para el aviso en vivo.
  const precioWatch = Form.useWatch("precio", form);
  const diasWatch = Form.useWatch("dias", form);
  const fillPriceWatch = Form.useWatch("fillPrice", form);
  const condValorWatch = Form.useWatch("condValor", form);
  // ¿El valor de condición está "en nada" (vacío o 0)? = SIN condición.
  const condVacia = condValorWatch == null || Number(condValorWatch) === 0;

  // Noches SIN precio dentro del rango+días elegidos (sólo relevante para minNoches
  // inline, donde tenemos `nightPrices`). Stays exige precio para fijar el mínimo;
  // estas se omitirían salvo que se les asigne un "Precio base" en el mismo paso.
  const pricelessCount = (() => {
    if (activeField !== "minNoches" || !nightPrices || !rango?.[0] || !rango?.[1]) return 0;
    const dias: number[] = Array.isArray(diasWatch) ? diasWatch : ALL_DAYS;
    let cur = rango[0].startOf("day");
    const end = rango[1].startOf("day");
    let count = 0;
    let guard = 0;
    while ((cur.isBefore(end) || cur.isSame(end, "day")) && guard < 1000) {
      guard += 1;
      if (dias.includes(cur.day())) {
        const key = cur.format("YYYY-MM-DD");
        // Sólo contamos noches CONOCIDAS sin precio (clave presente y null/≤0). Las
        // fechas fuera de la ventana cargada tienen clave ausente (undefined): NO se
        // cuentan, porque no conocemos su precio (el backend lo lee en vivo). Evita
        // inflar el conteo al extender el rango más allá de lo cargado.
        if (key in nightPrices) {
          const p = nightPrices[key];
          if (p == null || Number(p) <= 0) count += 1;
        }
      }
      cur = cur.add(1, "day");
    }
    return count;
  })();
  // Mostrar el input de "precio base para noches sin precio": inline cuando hay
  // noches sin precio; masivo (sin mapa por-noche) siempre, como opción.
  const showFillPrice =
    activeField === "minNoches" && (pricelessCount > 0 || (isBulk && nightPrices == null));

  // Carga del umbral global (una vez por sesión, cache de módulo).
  useEffect(() => {
    if (!open) return;
    let activo = true;
    loadUmbralPrecioBajo().then((u) => {
      if (activo) setUmbral(u);
    });
    return () => {
      activo = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setActiveField(field ?? "base");
    setSelectedIds([]);
    // Precarga de la condición vigente (re-edición inline) para NO invertir el
    // signo: si la noche ya tiene −20%, abrir en Promoción/−/20, no en Adición/+.
    // Sólo aplica a field=condicion; en otros campos se ignora.
    const ic = field === "condicion" ? (initialCondicion ?? null) : null;
    setCondDir(ic && ic.valor < 0 ? "promo" : "adicion");
    setCondTipo(ic?.tipo ?? "porcentaje");
    form.setFieldsValue({
      rango: dateRange
        ? [dayjs(dateRange[0], "YYYY-MM-DD"), dayjs(dateRange[1], "YYYY-MM-DD")]
        : null,
      dias: ALL_DAYS,
      precio: undefined,
      condValor: ic ? Math.abs(ic.valor) : undefined,
      minNoches: undefined,
      fillPrice: undefined,
      restriccion: "none",
    });
  }, [open, dateRange, field, form, initialCondicion]);

  // En bulk, al cambiar el campo a editar limpiamos los inputs específicos del
  // campo anterior (quedan ocultos por los condicionales, pero así no se arrastra
  // estado viejo si se refactoriza buildValue).
  useEffect(() => {
    if (isBulk) {
      form.setFieldsValue({
        precio: undefined,
        condValor: undefined,
        minNoches: undefined,
        fillPrice: undefined,
      });
    }
  }, [activeField, isBulk, form]);

  if (!open || (!field && !isBulk)) return null;
  const info = FIELD_INFO[activeField];
  const effectiveIds = isBulk ? selectedIds : propertyIds;

  // ─── Guardas de precio por noche ─────────────────────────────────────────
  // (1) Mínimo ACORDADO con el propietario (por propiedad). En inline viene en
  //     `propertyMin`; en masivo, por propiedad en `bulkProperties`.
  // (2) Umbral GLOBAL de precio bajo (`umbral`) — red anti-error de tipeo.
  // Ambas sólo aplican al campo "base". No bloquean: piden confirmación.
  const relevantMins: { id: number; nombre: string; min: number }[] = isBulk
    ? (bulkProperties ?? [])
        .filter((p) => effectiveIds.includes(p.id) && p.precioMinimoNoche != null)
        .map((p) => ({ id: p.id, nombre: p.nombre, min: p.precioMinimoNoche as number }))
    : propertyMin != null
      ? [{ id: propertyIds[0] ?? 0, nombre: propertyLabel ?? "esta propiedad", min: propertyMin }]
      : [];

  const computePriceFlags = (base: number) => {
    const minViolations = relevantMins.filter((v) => base < v.min);
    const belowUmbral = base < umbral;
    return { minViolations, belowUmbral };
  };

  // Confirmación extra al aplicar un precio base bajo. Resuelve `true` si el
  // admin decide aplicar igual, `false` si cancela. Si no hay bridge de Modal
  // estático, no bloquea (resuelve true).
  const confirmLowPrice = (
    base: number,
    minViolations: { id: number; nombre: string; min: number }[],
    belowUmbral: boolean,
  ): Promise<boolean> =>
    new Promise((resolve) => {
      const modal = getModal();
      if (!modal) {
        resolve(true);
        return;
      }
      modal.confirm({
        title: `Precio por noche bajo: ${clp(base)}`,
        okText: "Sí, aplicar de todos modos",
        okButtonProps: { danger: true },
        cancelText: "Cancelar",
        width: 480,
        content: (
          <div style={{ fontSize: 13 }}>
            {minViolations.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <b>Por debajo del mínimo acordado con el propietario:</b>
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {minViolations.map((v) => (
                    <li key={v.id}>
                      {formatPropertyName(v.nombre)}: mínimo {clp(v.min)}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: 4 }}>
                  Aplicarlo pasa a llevar ese acuerdo. Se registrará una alerta
                  para el admin.
                </div>
              </div>
            )}
            {belowUmbral && (
              <div>
                Es un precio inusualmente bajo (umbral {clp(umbral)}). Revisa que
                no sea un error de tipeo.
              </div>
            )}
          </div>
        ),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const buildValue = (values: any): any => {
    if (activeField === "base") return Math.round(Number(values.precio));
    if (activeField === "minNoches") return Number(values.minNoches);
    if (activeField === "restriccion")
      return values.restriccion === "none" ? null : values.restriccion;
    // condicion — 0 o vacío = SIN condición (efectivo = base; no se toca el
    // precio base). Devolvemos null para QUITAR cualquier condición vigente,
    // así el admin puede dejar el rango "sin condición" desde el propio campo
    // sin tener que usar el botón "Quitar condición" (pedido del owner
    // 2026-07-03). El backend además normaliza valor 0 → null.
    const raw = values.condValor;
    if (raw == null || raw === "" || Number(raw) === 0) return null;
    const mag = Math.abs(Number(raw));
    const signed = condDir === "promo" ? -mag : mag;
    return { tipo: condTipo, valor: signed };
  };

  const submit = async (clearCondicion = false) => {
    try {
      if (isBulk && effectiveIds.length === 0) {
        getMsg()?.warning("Selecciona al menos una propiedad");
        return;
      }
      let values: any;
      if (clearCondicion) {
        await form.validateFields(["rango", "dias"]);
        values = form.getFieldsValue();
      } else {
        values = await form.validateFields();
      }
      const [from, to] = values.rango;
      // Precio base para noches SIN precio (sólo minNoches, opcional). Se manda al
      // backend, que lo usa como `_f_val` SÓLO en las noches vacías del rango.
      const fillPriceVal =
        activeField === "minNoches" && values.fillPrice != null && Number(values.fillPrice) > 0
          ? Math.round(Number(values.fillPrice))
          : null;
      const payload = {
        propertyIds: effectiveIds,
        field: activeField,
        from: from.format("YYYY-MM-DD"),
        to: to.format("YYYY-MM-DD"),
        days: values.dias,
        value: clearCondicion ? null : buildValue(values),
        ...(fillPriceVal != null ? { fillPrice: fillPriceVal } : {}),
      };

      // Guarda de precio bajo (sólo al fijar el precio base). Si está bajo el
      // mínimo acordado con el propietario y/o bajo el umbral global, pedimos
      // una confirmación extra antes de empujar a Stays. El admin puede aplicar
      // igual (queda registrado: el backend levanta una alerta si va bajo el
      // mínimo acordado).
      // EXCEPCIÓN base=0 (2026-07-02): 0 significa INHABILITAR la noche en
      // Stays — es intencional, no un "precio bajo", así que en vez de las
      // guardias de precio va una confirmación dedicada que explica el efecto.
      if (!clearCondicion && activeField === "base") {
        const base = Number(payload.value);
        if (base === 0) {
          const proceed = await new Promise<boolean>((resolve) => {
            const modal = getModal();
            if (!modal) {
              resolve(true);
              return;
            }
            modal.confirm({
              title: `Inhabilitar ${nNoches} noche${nNoches === 1 ? "" : "s"} (precio $0)`,
              okText: "Sí, inhabilitar",
              okButtonProps: { danger: true },
              cancelText: "Cancelar",
              width: 480,
              content: (
                <div style={{ fontSize: 13 }}>
                  Precio <b>$0</b> = se creará un <b>BLOQUEO</b> en el calendario
                  y en Stays para esas noches (mecanismo oficial de Stays; los
                  precios no se tocan). Para volver a habilitarlas, elimina el
                  bloqueo como cualquier otro. Si alguna noche choca con un
                  arriendo/aseo/bloqueo existente, ese tramo se omite y se
                  informa.
                </div>
              ),
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!proceed) return; // canceló: dejamos el modal abierto
        } else {
          const { minViolations, belowUmbral } = computePriceFlags(base);
          if (minViolations.length > 0 || belowUmbral) {
            const proceed = await confirmLowPrice(base, minViolations, belowUmbral);
            if (!proceed) return; // canceló: dejamos el modal abierto
          }
        }
      }

      // Misma guarda de precio bajo para el "precio base de noches sin precio"
      // (fillPrice) al fijar el mínimo: atrapa typos y precios bajo el mínimo acordado.
      if (!clearCondicion && activeField === "minNoches" && fillPriceVal != null) {
        const { minViolations, belowUmbral } = computePriceFlags(fillPriceVal);
        if (minViolations.length > 0 || belowUmbral) {
          const proceed = await confirmLowPrice(fillPriceVal, minViolations, belowUmbral);
          if (!proceed) return;
        }
      }

      setLoading(true);
      const result = await apiFetch<{
        success?: boolean;
        message?: string;
        /** true cuando precio 0 → se crearon BLOQUEOS (inhabilitar noches). */
        bloqueos?: boolean;
        resultados?: Array<{ confirmado?: boolean; ok?: boolean }>;
      }>("/stays/calendario/meta", {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      if (result !== null) {
        const single =
          result.resultados?.length === 1 ? result.resultados[0] : null;
        const notConfirmed = single ? single.confirmado === false : false;
        if (result.success) {
          if (notConfirmed) {
            getMsg()?.warning(
              result.message ??
                "Enviado, pero Stays no lo confirmó. Revisa el anuncio (publicación / disponibilidad).",
              10,
            );
          } else {
            getMsg()?.success(result.message ?? "Actualizado en Stays");
          }
          onSuccess?.(result);
          onClose();
        } else {
          // Falló en todas las propiedades: dejamos el modal abierto para reintentar.
          getMsg()?.error(result.message ?? "No se pudo aplicar el cambio en Stays.");
        }
      }
    } catch (err: any) {
      if (!err?.errorFields) {
        getMsg()?.error(err?.message ?? "Error al actualizar");
      }
    } finally {
      setLoading(false);
    }
  };

  // Los dos botones que envían van deshabilitados mientras el rango exceda (los
  // dos usan el mismo rango contra el mismo endpoint). El `disabled` va TAMBIÉN
  // en el Popconfirm: AntD envuelve un botón deshabilitado en un span que sigue
  // recibiendo el clic, así que sin esto la confirmación se abriría igual y
  // desde ahí "Sí, aplicar" llamaría a submit.
  const footer = [
    <Button key="cancel" onClick={onClose}>
      Cancelar
    </Button>,
    activeField === "condicion" ? (
      <Popconfirm
        key="clear"
        title="¿Quitar la condición de estas fechas?"
        onConfirm={() => submit(true)}
        okText="Sí, quitar"
        cancelText="Cancelar"
        disabled={rangoExcedido}
      >
        <Button danger loading={loading} disabled={rangoExcedido}>
          Quitar condición
        </Button>
      </Popconfirm>
    ) : null,
    <Popconfirm
      key="submit"
      title="¿Confirmas? Esto modifica el calendario en Stays."
      onConfirm={() => submit(false)}
      okText="Sí, aplicar"
      cancelText="Cancelar"
      disabled={rangoExcedido}
    >
      <Button type="primary" loading={loading} disabled={rangoExcedido}>
        Aplicar
      </Button>
    </Popconfirm>,
  ].filter(Boolean);

  return (
    <Modal
      title={isBulk ? "Cambio masivo de tarifas" : info.title}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={footer}
      width={isBulk ? 560 : 480}
    >
      {isBulk && (
        <>
          <Form.Item label="Qué editar" style={{ marginBottom: 12 }}>
            <Segmented
              value={activeField}
              onChange={(v) => setActiveField(v as MetaField)}
              options={FIELD_OPTS}
              block
            />
          </Form.Item>
          <Form.Item label="Propiedades" style={{ marginBottom: 8 }}>
            <Select
              mode="multiple"
              allowClear
              placeholder="Selecciona propiedades…"
              value={selectedIds}
              onChange={(v) => setSelectedIds(v as number[])}
              options={bulkOpts}
              // fuzzyIncludes pliega tildes/ñ ("cabana" encuentra "CABAÑA").
              filterOption={(input, option) =>
                fuzzyIncludes(option?.label, input)
              }
              style={{ width: "100%" }}
              maxTagCount="responsive"
            />
          </Form.Item>
          <Space wrap style={{ marginBottom: 12 }}>
            <Button size="small" onClick={() => setSelectedIds(bulkOpts.map((o) => o.value))}>
              Todas ({bulkOpts.length})
            </Button>
            <Button size="small" onClick={() => setSelectedIds([])}>
              Ninguna
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {selectedIds.length} seleccionada(s)
            </Text>
          </Space>
        </>
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          isBulk
            ? FIELD_INFO[activeField].title
            : propertyLabel || "Propiedad seleccionada"
        }
        description={info.help}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="rango"
          label="Rango de fechas"
          rules={[{ required: true, message: "Seleccione un rango de fechas" }]}
          extra={
            nNoches > 0 ? (
              <>
                <Text type={nNoches === 1 ? "secondary" : "warning"}>
                  Se editará{nNoches === 1 ? "" : "n"} {nNoches} noche{nNoches === 1 ? "" : "s"}
                  {nNoches > 1 ? " (incluye ambos extremos)" : ""}.
                </Text>
                {/* Rango más largo de lo que se puede aplicar de una vez: el
                    aviso va junto al conteo de noches (no lo reemplaza) y el
                    envío queda deshabilitado. */}
                {rangoExcedido && (
                  <div style={{ marginTop: 4 }}>
                    <Text type="danger">{MENSAJE_RANGO_TARIFAS_MAXIMO}</Text>
                  </div>
                )}
              </>
            ) : null
          }
        >
          <RangePicker format="DD-MM-YYYY" style={{ width: "100%" }} />
        </Form.Item>

        {activeField === "base" && (
          <>
            <Form.Item
              name="precio"
              label="Precio base por noche (CLP)"
              rules={[{ required: true, message: "Ingrese el precio base" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                // min=0 a propósito (pedido del dueño 2026-07-02): el 0 está
                // permitido = INHABILITAR la noche en Stays. Al aplicar hay una
                // confirmación dedicada (ver submit).
                min={0}
                formatter={clpInputFormatter}
                parser={clpInputParser}
                placeholder="Precio base por noche"
              />
            </Form.Item>
            {/* Aviso EN VIVO para el caso 0 = inhabilitar. */}
            {typeof precioWatch === "number" && precioWatch === 0 && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: -8, marginBottom: 16 }}
                message="Precio 0 = inhabilitar (se crea un bloqueo)"
                description="Al aplicar, se crea un BLOQUEO en el calendario y en Stays para esas noches (los precios no se tocan). Para volver a habilitarlas, elimina el bloqueo como cualquier otro."
              />
            )}
            {/* Aviso EN VIVO mientras el admin escribe: si el precio cae bajo el
                mínimo acordado o bajo el umbral global, se advierte antes de
                aplicar (al aplicar habrá además una confirmación). */}
            {typeof precioWatch === "number" &&
              precioWatch > 0 &&
              (() => {
                const base = Math.round(precioWatch);
                const { minViolations, belowUmbral } = computePriceFlags(base);
                if (minViolations.length === 0 && !belowUmbral) return null;
                return (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: -8, marginBottom: 16 }}
                    message="Precio por noche bajo"
                    description={
                      <div style={{ fontSize: 12 }}>
                        {minViolations.length > 0 && (
                          <div>
                            Bajo el mínimo acordado:{" "}
                            {minViolations
                              .map((v) => `${formatPropertyName(v.nombre)} (${clp(v.min)})`)
                              .join(", ")}
                            .
                          </div>
                        )}
                        {belowUmbral && (
                          <div>
                            Bajo el umbral de {clp(umbral)} — al aplicar se pedirá
                            confirmación.
                          </div>
                        )}
                      </div>
                    }
                  />
                );
              })()}
          </>
        )}

        {activeField === "condicion" && (
          <>
            <Form.Item label="Tipo de ajuste">
              <Space wrap>
                <Segmented
                  value={condDir}
                  onChange={(v) => setCondDir(v as "promo" | "adicion")}
                  options={[
                    { label: "Promoción (baja −)", value: "promo" },
                    { label: "Adición (alza +)", value: "adicion" },
                  ]}
                />
                <Segmented
                  value={condTipo}
                  onChange={(v) => setCondTipo(v as "porcentaje" | "monto")}
                  options={[
                    { label: "%", value: "porcentaje" },
                    { label: "$ monto", value: "monto" },
                  ]}
                />
              </Space>
            </Form.Item>
            <Form.Item
              name="condValor"
              label={condTipo === "porcentaje" ? "Porcentaje" : "Monto (CLP)"}
              // NO required y NO se rechaza el 0: dejar la condición "en nada"
              // (vacío o 0) es una opción válida = SIN condición = no tocar el
              // precio base (pedido del owner 2026-07-03). Solo evitamos
              // negativos (el InputNumber ya lo impide con min=0).
              rules={[
                {
                  validator: (_, v) =>
                    v == null || Number(v) >= 0
                      ? Promise.resolve()
                      : Promise.reject(new Error("No puede ser negativo")),
                },
              ]}
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Déjalo en <b>0</b> o <b>vacío</b> para dejar estas fechas SIN
                  condición (efectivo = base).
                </Text>
              }
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                max={condTipo === "porcentaje" ? 1000 : undefined}
                formatter={condTipo === "monto" ? clpInputFormatter : undefined}
                parser={condTipo === "monto" ? clpInputParser : undefined}
                addonAfter={condTipo === "porcentaje" ? "%" : "CLP"}
                placeholder={
                  condTipo === "porcentaje" ? "Ej. 10 (0 = sin condición)" : "Ej. 5.000 (0 = sin condición)"
                }
              />
            </Form.Item>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {condVacia ? (
                <>
                  <b>Sin condición</b>: el efectivo queda igual al precio base (no
                  se toca). Aplicar con 0 o el campo vacío quita cualquier
                  condición vigente en estas fechas.
                </>
              ) : (
                <>
                  {condDir === "promo" ? "Baja" : "Alza"} el precio efectivo en{" "}
                  {condTipo === "porcentaje" ? "porcentaje" : "un monto fijo"}. El
                  base no cambia; a Stays va el efectivo. Si alguna noche quedara
                  con efectivo $0 o negativo, el cambio se bloquea (mínimo $1).
                </>
              )}
            </Text>
          </>
        )}

        {activeField === "minNoches" && (
          <>
            <Form.Item
              name="minNoches"
              label="Mínimo de noches"
              rules={[{ required: true, message: "Ingrese el mínimo de noches" }]}
            >
              <InputNumber style={{ width: "100%" }} min={1} step={1} placeholder="Ej. 2" />
            </Form.Item>
            {/* Stays exige que la noche tenga precio para poder fijarle un mínimo. Si
                el rango incluye noches SIN precio (detectable en inline con
                nightPrices), ofrecemos asignarles un "Precio base" en el mismo paso.
                En masivo (sin mapa por-noche) va siempre, opcional. */}
            {showFillPrice && (
              <>
                <Form.Item
                  name="fillPrice"
                  label="Precio base para noches sin precio (CLP)"
                  // OBLIGATORIO en inline cuando hay noches sin precio: Stays exige
                  // un precio para poder fijar el mínimo, así que no se deja aplicar
                  // hasta ingresarlo. En bulk (pricelessCount=0, no hay mapa por-noche)
                  // queda opcional: no sabemos si alguna propiedad tiene noches vacías.
                  rules={[
                    {
                      required: pricelessCount > 0,
                      message:
                        "Estas noches no tienen precio: asígnales uno para poder fijar el mínimo.",
                    },
                  ]}
                  extra={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {isBulk
                        ? "Opcional. Se aplica SÓLO a noches sin precio (las que ya tienen conservan el suyo); si lo dejas vacío, esas noches se omiten."
                        : `Obligatorio: ${pricelessCount} noche${pricelessCount === 1 ? "" : "s"} del rango no ${pricelessCount === 1 ? "tiene" : "tienen"} precio. Stays exige un precio para fijar el mínimo, así que debes asignarles uno (las que ya tienen precio conservan el suyo).`}
                    </Text>
                  }
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={1}
                    formatter={clpInputFormatter}
                    parser={clpInputParser}
                    placeholder="Precio para las noches sin precio"
                  />
                </Form.Item>
                {/* Aviso EN VIVO de precio bajo para el fillPrice (mismo criterio que 'base'). */}
                {typeof fillPriceWatch === "number" &&
                  fillPriceWatch > 0 &&
                  (() => {
                    const base = Math.round(fillPriceWatch);
                    const { minViolations, belowUmbral } = computePriceFlags(base);
                    if (minViolations.length === 0 && !belowUmbral) return null;
                    return (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: -8, marginBottom: 16 }}
                        message="Precio por noche bajo"
                        description={
                          <div style={{ fontSize: 12 }}>
                            {minViolations.length > 0 && (
                              <div>
                                Bajo el mínimo acordado:{" "}
                                {minViolations
                                  .map((v) => `${formatPropertyName(v.nombre)} (${clp(v.min)})`)
                                  .join(", ")}
                                .
                              </div>
                            )}
                            {belowUmbral && (
                              <div>
                                Bajo el umbral de {clp(umbral)} — al aplicar se pedirá
                                confirmación.
                              </div>
                            )}
                          </div>
                        }
                      />
                    );
                  })()}
              </>
            )}
          </>
        )}

        {activeField === "restriccion" && (
          <Form.Item name="restriccion" label="Restricción">
            <Segmented options={RESTRICCION_OPTS} />
          </Form.Item>
        )}

        <Form.Item
          name="dias"
          label="Días de la semana"
          rules={[{ required: true, message: "Seleccione al menos un día" }]}
        >
          <Checkbox.Group options={DAY_OPTIONS} />
        </Form.Item>
        <Space wrap>
          <Button size="small" onClick={() => form.setFieldsValue({ dias: PRESET_DJ })}>
            D-J
          </Button>
          <Button size="small" onClick={() => form.setFieldsValue({ dias: PRESET_VS })}>
            V-S
          </Button>
          <Button size="small" onClick={() => form.setFieldsValue({ dias: ALL_DAYS })}>
            Todos
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            D-J = Domingo a Jueves · V-S = Viernes y Sábado
          </Text>
        </Space>
      </Form>
    </Modal>
  );
}
